import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { casualPayrollApi, F93D } from '../../../services/casualPayrollApi';

export type Mode = 'browse' | 'add' | 'edit';

export function useCasualPayrollForm() {
  const [mode, setMode] = useState<Mode>('browse');
  const [records, setRecords] = useState<F93D[]>([]);
  const [currentIdx, setCurrentIdx] = useState(-1);
  const [loading, setLoading] = useState(false);

  const [code, setCode] = useState('');
  const [name, setName] = useState('');
  const [startYM, setStartYM] = useState('');
  const [endYM, setEndYM] = useState('');
  const [salary, setSalary] = useState(0);
  const [meal, setMeal] = useState(0);
  const [overtime, setOvertime] = useState(0);
  const [tax, setTax] = useState(0);
  const [labor, setLabor] = useState(0);
  const [health, setHealth] = useState(0);
  const [pension, setPension] = useState(0);

  const current = currentIdx >= 0 && currentIdx < records.length ? records[currentIdx] : null;
  const isEditing = mode !== 'browse';

  const loadRecords = useCallback(async () => {
    setLoading(true);
    try {
      const res = await casualPayrollApi.list(1, 500);
      setRecords(res.data || []);
      return res.data || [];
    } catch { message.error('載入失敗'); return []; }
    finally { setLoading(false); }
  }, []);

  const syncFrom = useCallback((r: F93D) => {
    setCode(r.f93d_01); setName(r.f93d_11);
    setStartYM(r.f93d_12); setEndYM(r.f93d_13);
    setSalary(r.f93d_31); setMeal(r.f93d_32); setOvertime(r.f93d_33);
    setTax(r.f93d_36); setLabor(r.f93d_37); setHealth(r.f93d_38); setPension(r.f93d_39);
  }, []);

  useEffect(() => { if (current && mode === 'browse') syncFrom(current); }, [current, mode, syncFrom]);
  useEffect(() => { loadRecords().then(d => { if (d.length > 0) setCurrentIdx(0); }); }, [loadRecords]);

  const navFirst = () => { if (!isEditing && records.length > 0) setCurrentIdx(0); };
  const navPrev = () => { if (!isEditing && currentIdx > 0) setCurrentIdx(currentIdx - 1); };
  const navNext = () => { if (!isEditing && currentIdx < records.length - 1) setCurrentIdx(currentIdx + 1); };
  const navLast = () => { if (!isEditing && records.length > 0) setCurrentIdx(records.length - 1); };

  const startAdd = () => {
    setMode('add'); setCode(''); setName(''); setStartYM(''); setEndYM('');
    setSalary(0); setMeal(0); setOvertime(0); setTax(0); setLabor(0); setHealth(0); setPension(0);
  };
  const startEdit = () => { if (current) { setMode('edit'); syncFrom(current); } };
  const cancel = () => { setMode('browse'); if (current) syncFrom(current); };

  const save = async () => {
    const payload = { f93d_11: name, f93d_12: startYM, f93d_13: endYM,
      f93d_31: salary, f93d_32: meal, f93d_33: overtime,
      f93d_36: tax, f93d_37: labor, f93d_38: health, f93d_39: pension };
    try {
      if (mode === 'add') {
        await casualPayrollApi.create({ f93d_01: code, ...payload });
        message.success('新增成功');
      } else {
        await casualPayrollApi.update(current!.f93d_01, payload);
        message.success('修改成功');
      }
      const data = await loadRecords();
      const tc = mode === 'add' ? code : current!.f93d_01;
      const idx = data.findIndex((r: F93D) => r.f93d_01 === tc);
      setCurrentIdx(idx >= 0 ? idx : 0);
      setMode('browse');
    } catch (err: any) {
      const vErrs = err?.response?.data?.errors;
      if (vErrs && Array.isArray(vErrs)) vErrs.forEach((e: any) => message.error(e.message));
      else message.error(err?.response?.data?.error || '儲存失敗');
    }
  };

  const doDelete = async () => {
    if (!current) return;
    try {
      await casualPayrollApi.delete(current.f93d_01);
      message.success('刪除成功');
      const data = await loadRecords();
      if (data.length > 0) setCurrentIdx(Math.min(currentIdx, data.length - 1));
      else setCurrentIdx(-1);
    } catch { message.error('刪除失敗'); }
  };

  const quickSearch = (t: string) => {
    if (isEditing) return;
    const idx = records.findIndex(r => r.f93d_01 >= t);
    if (idx >= 0) setCurrentIdx(idx);
  };

  return {
    mode, isEditing, loading, records, current, currentIdx,
    code, setCode, name, setName, startYM, setStartYM, endYM, setEndYM,
    salary, setSalary, meal, setMeal, overtime, setOvertime,
    tax, setTax, labor, setLabor, health, setHealth, pension, setPension,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save, doDelete, quickSearch,
  };
}
